import * as React from "react";
import {cx} from "emotion";

import "./type-url.scss";

import {CachedLoader}        from "@Common/react/cached-loader";
import {cs}                  from "@Common/react/chain-services";
import {consumeContext}      from "@Common/react/context";
import {resolveErrorMessage} from "@Common/react/cs-form-2/resolve-error-message";
import {required}            from "@Common/react/cs-form-2/validators/required";
import {
    urlFormatValidation,
    validateUrlFormat
}                            from "@Common/react/cs-form-2/validators/url-format-validation";
import {DebounceCache}       from "@Common/react/debounce-cache";
import {fragments}           from "@Common/react/fragments";
import {Invoke}              from "@Common/react/invoke";
import {KeyedState}          from "@Common/react/keyed-state";
import {scopedState}         from "@Common/react/scoped-state";
import {scnv}                from "@Common/react/state-change-nested-value";
import {Static}              from "@Common/react/static";
import {Static2}             from "@Common/react/static-2";
import {UseState}            from "@Common/react/use-state";
import {cnv}                 from "@Common/utils/arr-path";
import {chain}               from "@Common/utils/fs";

import {ErrorHighlighter}  from "../../../../../../light/error-highlighter";
import {scrollIntoView}    from "../../../../../../light/scroll-into-view";
import {Checkbox}          from "../../../../../common/design2/checkbox/checkbox";
import {setCurrentToSaved} from "../../../set-current-to-saved";

export const TypeUrl = ({disabled, liveValue, path, readOnly=false, onChange=(e)=>{}, next}) => cs(
    ["initValue", (_, next) => Static({
        initValue: liveValue,
        next,
    })],
    ["urlState", ({initValue}, next) => UseState({
        initValue: chain(
            initValue,
            (_) => ({
                current: _,
                saved  : _,
            })
        ),
        next,
    })],

    ["syncError", ({urlState}, next) => next(
        !disabled && (
            resolveErrorMessage(urlState.value.current, [
                required,
                {
                    message: "URL must start with https://",
                    validate: (v) => !v || v.startsWith("https://"),
                },
                urlFormatValidation,
            ])
        )
    )],
    consumeContext("apis"),
    ["liveValidation", ({urlState, syncError, apis}, next) => CachedLoader({
        disabled: syncError,
        _key: urlState.value.current,
        fetch: async (url) => await apis.next.networking.validateUrl({url}),
        next,
    })],

    ["eh", (_, next) => ErrorHighlighter({next})],
    ["domRef", (_, next) => Static2({next})],

    ["focused", (_, next) => UseState({next})],

    consumeContext("session"),
    ({session, urlState}, next) => fragments(
        next(),
        session.data?.agent?.agentDisplay8 && Invoke({
            action: () => {
                if (!urlState.value?.current && validateUrlFormat(session.data.agent.agentDisplay8)) {
                    scnv(urlState, ["current"], () => session.data.agent.agentDisplay8);
                }
                // console.log("agent", session.data?.agent);
            },
        })
    ),

    ["acceptedValidationError", ({urlState, initValue}, next) => KeyedState({
        _key: urlState.value.current,
        defaultValue: initValue === urlState.value.current,
        next,
    })],
    ({eh, domRef, urlState, syncError, liveValidation, focused, acceptedValidationError}) => next({
        editor: !disabled && {
            $name: "Informed Delivery > Redirect URL > url",
            valid: !syncError && (acceptedValidationError.value || liveValidation.value?.success),
            loading: !acceptedValidationError.value && liveValidation.loading,

            changed: urlState.value?.saved !== urlState.value?.current,
            prepareSave: (data) => ({
                newValue: chain(
                    data,
                    (_) => cnv(_, path, () => urlState.value?.current),
                ),
                onSaved: () => setCurrentToSaved(urlState),
            }),

            errorsHandling: {
                highlightErrors: () => {
                    scrollIntoView(domRef.get());
                    eh.start();
                },
            },
        },
        activeUrl: urlState.value?.current,
        renderControl: ({showErrors}) => cs(
            ["debounce", (_, next) => DebounceCache({
                state: scopedState(urlState, ["current"]),
                next,
            })],
            ({debounce}) => (
                <div className="type-url-82k main">
                    <input {...{
                        className: cx(eh.css, {
                            hasError: showErrors && !debounce.active && (
                                syncError ||
                                // (!liveValidation.loading && !liveValidation.value) ||
                                false
                            ),
                        }),
                        placeholder: "https://",
                        readOnly: readOnly,
                        value: debounce.state.value,
                        onChange: (e) => {
                            debounce.state.onChange(e.target.value);
                            onChange(e);
                        },
                        onFocus: () => {
                            focused.onChange(true);
                        },
                        onBlur: () => {
                            focused.onChange(false);
                            debounce.flush();
                        },
                        ref: domRef.set,

                        "xd-qa-type": "text_field",
                        "xd-qa-name": "Redirect URL Custom URL",
                    }}/>

                    {disabled ? (
                        null
                    ) : debounce.active ? (
                        <i className="fa fa-pencil"/>
                    ) : liveValidation.loading ? (
                        <i className="fa fa-spinner fa-pulse"/>
                    ) : !focused.value && (
                        liveValidation.value?.success ? (
                            <i className="fa fa-check"/>
                        ) : (
                            <i className={cx("fa fa-times", {highlighted: showErrors, inactive: acceptedValidationError.value})}/>
                        )
                    )}
                </div>
            ),
        ),
        errorHandling: !focused.value && (
            syncError ? {
                message: syncError,
            } : !liveValidation.loading && !liveValidation.value?.success && {
                message: "URL validation failed, if you believe this is a valid URL, check this box to proceed using it",
                controls: (
                    <div {...{
                        className: "type-url-82k error-handling-controls",
                        onClick: () => acceptedValidationError.change(v=>!v),
                    }}>
                        {Checkbox({
                            checked: acceptedValidationError.value,
                            xdQaName: "Redirect URL Skip Live Validation",
                        })}

                        <div className="message">
                            Continue with this URL
                        </div>
                    </div>
                )
            }
        ),
    }),
);

